import { useState, useRef, useEffect } from "react";
import { ToolsShell, primaryBtn, TOOLS_ACCENT } from "./ToolsShell.jsx";

// Tone Generator — Web Audio API oscillator. Logarithmic frequency
// slider so the low and high ends both have useful resolution. Volume
// defaults to moderate.

const WAVEFORMS = [
  { id: "sine", label: "Sine" },
  { id: "square", label: "Square" },
  { id: "triangle", label: "Triangle" },
  { id: "sawtooth", label: "Sawtooth" },
];

// Frequency slider goes 0-1000 (UI), mapped log to 20-20000 Hz.
const sliderToHz = (s) => Math.round(20 * Math.pow(1000, s / 1000));
const hzToSlider = (hz) => Math.round(1000 * Math.log(hz / 20) / Math.log(1000));

export default function ToneGenerator() {
  const [freq, setFreq] = useState(440);
  const [waveform, setWaveform] = useState("sine");
  const [volume, setVolume] = useState(0.15);
  const [playing, setPlaying] = useState(false);
  const ctxRef = useRef(null);
  const oscRef = useRef(null);
  const gainRef = useRef(null);

  const start = () => {
    if (playing) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = waveform;
      osc.frequency.value = freq;
      gain.gain.value = 0;
      gain.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.05);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      ctxRef.current = ctx; oscRef.current = osc; gainRef.current = gain;
      setPlaying(true);
    } catch {}
  };
  const stop = () => {
    if (!playing) return;
    try {
      const g = gainRef.current, ctx = ctxRef.current, osc = oscRef.current;
      if (g && ctx) {
        g.gain.cancelScheduledValues(ctx.currentTime);
        g.gain.setValueAtTime(g.gain.value, ctx.currentTime);
        g.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.05);
      }
      setTimeout(() => {
        try { osc && osc.stop(); } catch {}
        try { ctx && ctx.close(); } catch {}
      }, 80);
    } catch {}
    setPlaying(false);
  };

  // Live-update frequency/waveform/volume while playing
  useEffect(() => {
    if (oscRef.current) oscRef.current.frequency.setTargetAtTime(freq, ctxRef.current.currentTime, 0.02);
  }, [freq]);
  useEffect(() => {
    if (oscRef.current && playing) oscRef.current.type = waveform;
  }, [waveform, playing]);
  useEffect(() => {
    if (gainRef.current && ctxRef.current && playing) gainRef.current.gain.setTargetAtTime(volume, ctxRef.current.currentTime, 0.02);
  }, [volume, playing]);

  useEffect(() => () => { stop(); /* eslint-disable-next-line */ }, []);

  return (
    <ToolsShell title="Tone Generator">
      <div style={{ background: "#1F1418", border: "1px solid #E0584E33", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#D9A39E", marginBottom: 16 }}>
        <strong>Warning:</strong> sound can be loud. Start with low volume; some frequencies (especially high ones at high volume) can damage hearing or annoy pets.
      </div>

      <div style={{ background: "#13141F", border: `1px solid ${TOOLS_ACCENT}33`, borderRadius: 12, padding: "20px 22px", textAlign: "center" }}>
        <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 4 }}>Frequency</div>
        <div style={{ fontSize: 44, fontWeight: 800, color: TOOLS_ACCENT, fontVariantNumeric: "tabular-nums" }}>
          {freq} <span style={{ fontSize: 18, color: "#9A9DBE", fontWeight: 500 }}>Hz</span>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <input type="range" min="0" max="1000" value={hzToSlider(freq)} onChange={(e) => setFreq(sliderToHz(parseInt(e.target.value, 10)))} style={{ width: "100%", accentColor: TOOLS_ACCENT }} />
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#5B5E7A" }}>
          <span>20 Hz</span><span>440 Hz</span><span>20 kHz</span>
        </div>
        <input
          type="number"
          value={freq}
          min="20"
          max="20000"
          onChange={(e) => setFreq(Math.max(20, Math.min(20000, parseInt(e.target.value, 10) || 440)))}
          style={{ width: "100%", background: "#13141F", border: "1px solid #262839", borderRadius: 8, color: "#E8E9F3", fontSize: 14, padding: "8px 10px", boxSizing: "border-box", marginTop: 8, textAlign: "center" }}
        />
      </div>

      <div style={{ marginTop: 14 }}>
        <label style={{ fontSize: 13, color: "#9A9DBE", display: "block", marginBottom: 6 }}>Waveform</label>
        <div style={{ display: "flex", gap: 4 }}>
          {WAVEFORMS.map((w) => (
            <button
              key={w.id}
              onClick={() => setWaveform(w.id)}
              style={{ flex: 1, background: waveform === w.id ? TOOLS_ACCENT : "#1C1E2E", color: waveform === w.id ? "#1A0604" : "#9A9DBE", border: "none", borderRadius: 8, padding: "8px 4px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <label style={{ fontSize: 13, color: "#9A9DBE", display: "flex", justifyContent: "space-between" }}>
          <span>Volume</span>
          <span style={{ color: TOOLS_ACCENT }}>{Math.round(volume * 100)}%</span>
        </label>
        <input type="range" min="0" max="50" value={volume * 100} onChange={(e) => setVolume(parseInt(e.target.value, 10) / 100)} style={{ width: "100%", accentColor: TOOLS_ACCENT, marginTop: 4 }} />
      </div>

      <button onClick={playing ? stop : start} style={{ ...primaryBtn, width: "100%", marginTop: 18, background: playing ? "#1C1E2E" : TOOLS_ACCENT, color: playing ? TOOLS_ACCENT : "#1A0604" }}>
        {playing ? "Stop" : "Play"}
      </button>

      <p style={{ fontSize: 12, color: "#5B5E7A", marginTop: 14, textAlign: "center" }}>
        Useful for hearing tests (run a sweep), tuning instruments by ear, troubleshooting electronics, calming pets (low frequencies).
      </p>
    </ToolsShell>
  );
}
